import {
  amountToDisplayValue,
  Color,
  Flex,
  FontSize,
  LineHeight,
  pxToNumber,
  SkeletonBox,
  Spacing,
  Text,
} from '@billy/design-system'
import { lighten } from 'polished'
import React, { ReactElement, useCallback } from 'react'
import { Bar, Cell, ComposedChart, LabelList, LabelProps, ResponsiveContainer, XAxis, YAxis } from 'recharts'

import * as Styled from './styles'

const ANIMATION_TIME_MS = 750
const BAR_SIZE = 10
const LABEL_X = 5
const LABEL_ROW_HEIGHT = pxToNumber(LineHeight.TextBodyMD)
const X_AXIS_LABEL_FONT_SIZE = pxToNumber(FontSize.TextBodySM)

interface BarLabelProps extends LabelProps {
  index?: number
}

export interface BarChartData {
  label: string
  color: Color
  value: number
  displayValue?: number | string
  isBarHidden?: boolean
}

export interface BarChartProps {
  data: BarChartData[]
  isLoading?: boolean
  shortValueFormat?: boolean
}

export const BarChart = ({ data, isLoading, shortValueFormat }: BarChartProps): ReactElement => {
  const renderBarLabel = useCallback(
    (props: BarLabelProps) => {
      const { index, width, x, y } = props
      const barData = index === undefined ? undefined : data[index]

      if (!barData) {
        return null
      }

      const { color, label, displayValue, value } = barData

      const barEnd = (Number(x) || 0) + (Number(width) || 0)
      const barWidth = Math.max(0, barEnd - LABEL_X)
      const valueFormatted = amountToDisplayValue(Number(displayValue || value), shortValueFormat)

      return (
        <foreignObject height={LABEL_ROW_HEIGHT} width="100%" x={LABEL_X} y={Number(y) - LABEL_ROW_HEIGHT}>
          <Styled.BarLabelRowWrapper barWidth={barWidth}>
            <Flex alignItems="baseline" gap={Spacing.S8} justifyContent="space-between">
              <Text color={Color.Grey500} nowrap variant="bodySM">
                {label}
              </Text>
              <Text color={color} nowrap variant="bodyMD" weight="medium">
                {valueFormatted}
              </Text>
            </Flex>
          </Styled.BarLabelRowWrapper>
        </foreignObject>
      )
    },
    [data, shortValueFormat],
  )

  const generateGradient = useCallback(
    (index: number, color: Color) => (
      <linearGradient key={index} id={`gradient-${index}`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor={lighten(0.3, color)} />
        <stop offset="100%" stopColor={color} />
      </linearGradient>
    ),
    [],
  )

  const axisTickFormatter = (value: number) => amountToDisplayValue(value, shortValueFormat)

  return (
    <>
      {isLoading ? (
        <Styled.SkeletonWrapper>
          <Styled.SkeletonChartWrapper>
            <SkeletonBox fullHeight fullWidth />
          </Styled.SkeletonChartWrapper>
          <Styled.SkeletonXAxisWrapper>
            <SkeletonBox fullHeight fullWidth />
          </Styled.SkeletonXAxisWrapper>
        </Styled.SkeletonWrapper>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart layout="vertical" data={data}>
            {/* Gradient helpers: */}
            <defs>{data.map((entry, index) => generateGradient(index, entry.color))}</defs>

            {/* Chart: */}
            <XAxis
              type="number"
              tick={{ fill: Color.Grey500, fontSize: X_AXIS_LABEL_FONT_SIZE }}
              stroke={Color.Grey200}
              strokeWidth={1}
              tickMargin={5}
              tickFormatter={axisTickFormatter}
            />
            <YAxis dataKey="label" type="category" scale="band" hide />
            <Bar dataKey="value" barSize={BAR_SIZE} radius={BAR_SIZE / 2} animationDuration={ANIMATION_TIME_MS}>
              <LabelList content={renderBarLabel} />

              {data.map((barData, index) => {
                const cellProps = barData.isBarHidden ? { width: 0 } : {}
                return <Cell key={`cell-${index}`} fill={`url(#gradient-${index})`} {...cellProps} />
              })}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </>
  )
}
